const CHAT_CHECKS = new Set(['reliable_chat_fanout', 'chat_delivery_ledger', 'chat_delivery_recovery']);
const ISSUE_LABELS = {
    durable_interaction_acknowledgements: 'confirmação de persistência das interações',
    durable_acknowledgement_incomplete: 'confirmação de persistência das interações',
    chat_delivery_validation_incomplete: 'evidências de entrega e recuperação do chat',
    chat_delivery_evidence_incomplete: 'evidências de entrega do chat',
    chat_recovery_unconfirmed: 'recuperação das mensagens após a reconexão',
};
const INTEGRITY_LABELS = {
    duplicate_live_receipts: 'recibos ao vivo duplicados',
    cross_run_receipts: 'recibos de outra execução',
    cross_job_receipts: 'recibos de outro teste',
    invalid_delivery_evidence: 'evidências de entrega inválidas',
    recovered_chat_content_mismatch: 'conteúdo recuperado diferente do conteúdo enviado',
    chat_delivery_evidence_incomplete: 'evidências de entrega incompletas',
};

const issueLabel = (name) => CHAT_CHECKS.has(name)
    ? 'entrega e recuperação do chat'
    : ISSUE_LABELS[name] || name.replace(/_/g, ' ');

export const streamingLoadTestOutcome = (job = {}, report = {}) => {
    const ledger = report.fanout?.chat_delivery_ledger || {};
    const policy = report.chat_delivery_validation;
    const failedNames = Object.entries(report.checks || {})
        .filter(([, passed]) => passed === false).map(([name]) => name);
    // A saved legacy verdict stays unchanged. A new policy may deliberately
    // accept a raw delivery shortfall, so raw ledger status cannot override it.
    if (!policy && ledger.status === 'failed') failedNames.push('chat_delivery_ledger');
    const failedChecks = [...new Set(failedNames.map(issueLabel))];
    const jobStatus = job?.status;
    const cancelled = jobStatus === 'cancelled';
    const incomplete = !cancelled && report.validation?.status === 'incomplete' && failedChecks.length === 0;
    const incompleteReasons = [...new Set(Object.values(report.validation?.reasons || {})
        .flat().filter((reason) => typeof reason === 'string' && reason && !reason.endsWith('_not_requested')).map(issueLabel))];
    const warnings = [...new Set([...(Array.isArray(report.warnings) ? report.warnings : []),
        ...(Array.isArray(policy?.warnings) ? policy.warnings : [])])];
    const failed = !cancelled && !incomplete && (jobStatus === 'failed' || failedChecks.length > 0
        || report.validation?.status === 'failed' || report.passed === false);
    const completed = jobStatus === 'completed' && report.partial !== true;
    return {
        failedChecks, incompleteReasons, warnings,
        incomplete,
        failed,
        label: cancelled ? 'Encerrado' : incomplete ? 'Inconclusivo' : failed ? 'Reprovado'
            : completed ? warnings.length ? 'Concluído com avisos' : 'Concluído' : 'Encerrado',
        variant: failed ? 'danger' : 'primary',
    };
};

export const streamingChatDeliveryDescription = (report = {}) => {
    const ledger = report.fanout?.chat_delivery_ledger || {};
    const policy = report.chat_delivery_validation;
    const live = policy?.live_delivery;
    const expected = live?.expected_recipients ?? ledger.expectedRecipients ?? ledger.totals?.expected_recipients;
    const observed = live?.observed_recipients ?? ledger.observedRecipients ?? ledger.totals?.observed_recipients;
    if (!Number.isFinite(expected) || expected <= 0 || !Number.isFinite(observed)) return '';
    const percent = Number(live?.coverage_percent ?? observed / expected * 100)
        .toLocaleString('pt-BR', { maximumFractionDigits: 6 });
    const number = (value) => Number(value).toLocaleString('pt-BR');
    let description = `${number(observed)} de ${number(expected)} recibos de entrega ao vivo confirmados (${percent}%).`;
    if (Number.isFinite(live?.minimum_coverage_percent)) {
        description += ` Meta mínima: ${number(live.minimum_coverage_percent)}%.`;
    }
    if (expected > observed) description += ` ${number(expected - observed)} ${expected - observed === 1 ? 'recibo não confirmado' : 'recibos não confirmados'}.`;
    const recovery = policy?.recovery;
    if (Number(recovery?.required_recipients) > 0) {
        description += ` Recuperação: ${number(recovery.recovered_recipients)} de ${number(recovery.required_recipients)} confirmadas após saídas planejadas.`;
    }
    if (Number(live?.excluded_recovered_recipients) > 0) {
        description += ` ${number(live.excluded_recovered_recipients)} ${live.excluded_recovered_recipients === 1 ? 'entrega recuperada foi excluída' : 'entregas recuperadas foram excluídas'} da meta ao vivo.`;
    }
    const integrityReasons = Array.isArray(policy?.integrity?.reasons) ? policy.integrity.reasons : [];
    if (integrityReasons.length > 0) {
        description += ` Integridade: ${integrityReasons.map((reason) => INTEGRITY_LABELS[reason] || issueLabel(String(reason))).join(', ')}.`;
    }
    if (Number(recovery?.delayed_recipients) > 0) description += ' Há recuperações confirmadas após o prazo permitido.';
    return description;
};
